/**
 * ContractorOnboardingCockpit — Cockpit-language frame for /contractor-onboarding.
 *
 * KPIs project from OnboardingSession (canonical, per Sprint A.1).
 * Demo / seeding utilities live in a quarantined "Developer Tools"
 * section at the bottom of the page (Sprint A.6).
 */
import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CockpitPage, CockpitMasthead, HeroMetric, HeroDivider,
} from "@/components/cockpit/CockpitShell";
import ContractorOnboarding from "./ContractorOnboarding";
import { DemoOnboardingControls } from "@/components/contractor/onboarding/DemoOnboardingControls";
import { useOnboarding } from "@/data/contractor/onboarding/OnboardingProvider";
import {
  getOnboardingLifecycleRows,
  getOnboardingLifecycleStageCounts,
} from "@/data/contractor/onboarding/lifecycle";
import { FlaskConical } from "lucide-react";
import { UNIFIED_ONBOARDING_COCKPIT } from "@/config/onboardingCockpitFlags";

export default function ContractorOnboardingCockpit() {
  const showDeveloperTools = import.meta.env.DEV;
  const onboarding = useOnboarding();
  const [params, setParams] = useSearchParams();
  const selectedSession = UNIFIED_ONBOARDING_COCKPIT ? params.get("session") : null;
  const selectedToken = UNIFIED_ONBOARDING_COCKPIT ? params.get("token") : null;

  useEffect(() => {
    if (!UNIFIED_ONBOARDING_COCKPIT) return;
    if (selectedSession || selectedToken || params.get("noAuto") === "1") return;
    const rows = getOnboardingLifecycleRows(
      onboarding.inviteTokens,
      onboarding.sessions,
      { perspective: "admin" },
    );
    const priority = ["submitted", "under_review", "reupload_requested"];
    const target =
      rows.find((r) => priority.includes(r.stage) && r.sessionId) ??
      rows.find((r) => r.sessionId) ??
      rows.find((r) => r.tokenId);
    if (!target) return;
    const next = new URLSearchParams(params);
    if (target.sessionId) {
      next.set("session", target.sessionId);
      next.delete("token");
    } else {
      next.set("token", target.tokenId);
      next.delete("session");
    }
    setParams(next, { replace: true });
  }, [selectedSession, selectedToken, onboarding.inviteTokens, onboarding.sessions, params, setParams]);

  // Hero KPIs are intentionally distinct from the lifecycle stage strip
  // below: they answer "what's the cockpit-level health?" — total in
  // pipeline, terminal approved count, and the active-attention bucket
  // (submitted + under review). Per-stage counts live in the strip and
  // are not duplicated here.
  const stats = useMemo(() => {
    const rows = getOnboardingLifecycleRows(
      onboarding.inviteTokens,
      onboarding.sessions,
      { perspective: "admin" },
    );
    const c = getOnboardingLifecycleStageCounts(rows);
    return {
      total: rows.length,
      approved: c.approved,
      needsReview: c.submitted + c.under_review + c.reupload_requested,
    };
  }, [onboarding.inviteTokens, onboarding.sessions]);

  return (
    <CockpitPage>
      <CockpitMasthead
        title="Onboarding Pipeline"
        right={(
          <>
            <HeroMetric label="In pipeline" value={stats.total} hero />
            <HeroDivider />
            <HeroMetric label="Approved" value={stats.approved} tone="good" />
            <HeroMetric
              label="Needs review"
              value={stats.needsReview}
              tone={stats.needsReview > 0 ? "warn" : "default"}
            />
          </>
        )}
      />

      <div className="cockpit-surface -mx-6">
        <div className="px-6 space-y-6">
          <ContractorOnboarding />

          {showDeveloperTools ? (
            <details
              className="rounded-lg border border-dashed border-warning/40 bg-warning/[0.03]"
              data-testid="developer-tools-section"
            >
              <summary className="cursor-pointer px-4 py-2.5 flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground">
                <FlaskConical className="h-3.5 w-3.5 text-warning" />
                <span className="uppercase tracking-widest">Developer Tools</span>
                <span className="ml-2 font-normal normal-case tracking-normal">
                  Local Demo · Not Production — open to seed or reset
                </span>
              </summary>
              <div className="px-4 pb-4 pt-2">
                <DemoOnboardingControls />
              </div>
            </details>
          ) : null}
        </div>
      </div>
    </CockpitPage>
  );
}
